import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { TranslateLoader } from '@ngx-translate/core';
import { Observable, forkJoin, of } from 'rxjs';
import { map, catchError } from 'rxjs/operators';

/**
 * Multi Translate Loader - Loads multiple translation files per language
 * Supports module-wise translation files
 * Caches translations to avoid reloading when language hasn't changed
 */
@Injectable({
  providedIn: 'root'
})
export class MultiTranslateLoader implements TranslateLoader {
  private http = inject(HttpClient);

  // Cache to store loaded translations per language
  private translationCache: Map<string, any> = new Map();
  
  // Track the last loaded language
  private lastLoadedLanguage: string | null = null;

  // List of modules to load translations for
  private modules = [
    'common',
    'errors',
    'login',
    'dashboard',
    'patients',
    'appointments',
    'prescriptions',
    'billing',
    "consultation",
    'medical-records',
    'settings',
    'errorPage',
    'adminCommon'
  ];

  /**
   * Get translation for a language
   * Only loads JSON files if language has changed or translations are not cached
   * @param lang - Language code (e.g., 'en', 'mr', 'hi')
   * @returns Observable of merged translations
   */
  getTranslation(lang: string): Observable<any> {
    // Check if translations for this language are already cached
    if (this.translationCache.has(lang) && this.lastLoadedLanguage === lang) {
      // Return cached translations immediately
      return of(this.translationCache.get(lang));
    }

    // Language has changed or not cached, load all module translation files
    const translationFiles = this.modules.map(module => 
      this.http.get<any>(`./assets/i18n/${lang}/${module}.json`).pipe(
        map(translations => ({ [module]: translations })),
        // Handle errors gracefully - return empty object if file doesn't exist
        catchError(error => {
          console.warn(`Translation file not found: ${lang}/${module}.json`, error);
          return of({ [module]: {} });
        })
      )
    );

    // Merge all translations into a single object
    return forkJoin(translationFiles).pipe(
      map(translations => {
        const mergedTranslations = translations.reduce((acc, translation) => {
          return { ...acc, ...translation };
        }, {});

        // Cache the loaded translations
        this.translationCache.set(lang, mergedTranslations);
        this.lastLoadedLanguage = lang;

        return mergedTranslations;
      })
    );
  }

  /**
   * Clear translation cache for a specific language
   * @param lang - Language code to clear from cache
   */
  clearCache(lang?: string): void {
    if (lang) {
      this.translationCache.delete(lang);
      if (this.lastLoadedLanguage === lang) {
        this.lastLoadedLanguage = null;
      }
    } else {
      // Clear all cache
      this.translationCache.clear();
      this.lastLoadedLanguage = null;
    }
  }

  /**
   * Check if translations for a language are cached
   * @param lang - Language code to check
   * @returns True if cached, false otherwise
   */
  isCached(lang: string): boolean {
    return this.translationCache.has(lang) && this.lastLoadedLanguage === lang;
  }
}

